import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { API } from "@gui/infrastructure/pass-through/api";
import { useImpersonationContext } from "@gui/contexts/impersonation";
import type { ImpersonatableUser } from "@gui/infrastructure/pass-through/api";

export function useImpersonationSelector(companyId?: string, enabled = true) {
	const { impersonatedEmail, isImpersonating, startImpersonation, stopImpersonation } = useImpersonationContext();
	const [allUsers, setAllUsers] = useState<ImpersonatableUser[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const hasFetched = useRef(false);

	const fetchUsers = useCallback(async () => {
		if (hasFetched.current) return;
		setIsLoading(true);
		try {
			const result = await API.Users.getImpersonatableUsers();
			setAllUsers(result);
			hasFetched.current = true;
		} catch {
			// Error handled silently; dropdown will show empty state
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Pre-fetch on mount (only for eligible users)
	useEffect(() => {
		if (enabled) fetchUsers();
	}, [enabled, fetchUsers]);

	const users = useMemo(() => {
		if (!companyId) return allUsers;
		return allUsers.filter((u) => u.companyIds.includes(companyId));
	}, [allUsers, companyId]);

	// The picker's value: the listed user, or (before the list loads, or for a user outside this company) a stand-in
	// named by the email. The picker compares by email, so the stand-in still marks the right option.
	const impersonatedUser = useMemo<ImpersonatableUser | null>(() => {
		if (!impersonatedEmail) return null;
		return (
			users.find((u) => u.email === impersonatedEmail) ?? {
				id: impersonatedEmail,
				email: impersonatedEmail,
				name: impersonatedEmail,
				companyIds: [],
			}
		);
	}, [impersonatedEmail, users]);

	const onImpersonationPicked = useCallback(
		(picked: ImpersonatableUser | null) => {
			if (picked) startImpersonation(picked.email);
			else stopImpersonation();
		},
		[startImpersonation, stopImpersonation],
	);

	return {
		impersonatedEmail,
		impersonatedUser,
		isImpersonating,
		startImpersonation,
		stopImpersonation,
		onImpersonationPicked,
		users,
		isLoading,
		fetchUsers,
	};
}
